import { Coin } from '@repo/studio-design-system-ui-coin'

import { Button } from '@repo/studio-design-system-ui-button'
import { useCoinTransferAnimation } from '@repo/studio-hooks-coins/hooks/use-coin-transfer-animation'
import { formatCurrency } from '@repo/studio-utils/utils/currency'
import { CurrencyCode } from 'codegen/types'
import type { SubscriptionPlan } from 'codegen/types'
import { AnimatePresence, m } from 'framer-motion'
import { usePricesForUser } from '@repo/cf-user/hooks/use-prices'
import * as React from 'react'
import { useStudioTranslate } from '@repo/studio-config/studio-config-context'
import { tw } from 'utils/classnames'
import { SuccessIndicator } from '../success-indicator'
import { useQueryClient } from '@tanstack/react-query'
import { userQueryKey } from 'auth/hooks'
import { useCoinSubscriptionTier } from '@repo/studio-hooks-coins/hooks/use-coin-subscription-tier'
import { useStartCoinSubscription } from './use-start-coin-subscription'

interface CoinTopUpMessageProps {
  className?: string
  show: boolean
}

const SHOW_SUCCESS_TIME = 5000

export function CoinTopUpMessage({ className, show }: CoinTopUpMessageProps) {
  const { tier, subscriptionPlan, hasTargetPlan, priceCents, isCoinSubscriptionPending } =
    useCoinSubscriptionTier()

  if (isCoinSubscriptionPending || hasTargetPlan) {
    return null
  }

  return (
    <CoinSubscriptionMessage
      className={className}
      show={show}
      coins={tier.coins}
      priceCents={priceCents}
      subscriptionPlan={subscriptionPlan}
    />
  )
}

interface CoinSubscriptionMessageProps extends CoinTopUpMessageProps {
  coins: number
  priceCents: number
  subscriptionPlan: SubscriptionPlan
}

function CoinSubscriptionMessage({
  className,
  show,
  coins,
  priceCents,
  subscriptionPlan,
}: CoinSubscriptionMessageProps) {
  const t = useStudioTranslate()
  const { trigger } = useCoinTransferAnimation()
  const containerRef = React.useRef<HTMLFormElement | null>(null)
  const [showSuccess, setShowSuccess] = React.useState(false)
  const queryClient = useQueryClient()

  function onSuccess() {
    setShowSuccess(true)
    setTimeout(() => setShowSuccess(false), SHOW_SUCCESS_TIME)
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: userQueryKey })
    }, SHOW_SUCCESS_TIME)
    trigger({ source: containerRef.current })
  }

  const { handleSubmit, isPending } = useStartCoinSubscription(subscriptionPlan, { onSuccess })

  return (
    <CoinTopUpMessageLayout
      className={className}
      show={show}
      isPending={isPending}
      onSubmit={handleSubmit}
      containerRef={containerRef}
      coins={coins}
      priceCents={priceCents}
      priceLabel={t('studio.components.coin_top_up_message.coins_top_up.copy_1_subscription')}
      ctaLabel={t('studio.components.coin_top_up_message.coins_top_up.cta')}
      footerLabel={t('studio.components.coin_top_up_message.coins_top_up.copy_2_subscription')}
      showSuccess={showSuccess}
    />
  )
}

interface CoinTopUpMessageLayoutProps {
  className?: string
  show: boolean
  isPending: boolean
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  containerRef: React.RefObject<HTMLFormElement | null>
  coins: number
  priceCents: number
  priceLabel: string
  ctaLabel: string
  footerLabel: string
  showSuccess: boolean
}

function CoinTopUpMessageLayout({
  className,
  show,
  isPending,
  onSubmit,
  containerRef,
  coins,
  priceCents,
  priceLabel,
  ctaLabel,
  footerLabel,
  showSuccess,
}: CoinTopUpMessageLayoutProps) {
  const t = useStudioTranslate()
  const { currency } = usePricesForUser()
  const displayCurrencyCode = currency.code ?? CurrencyCode.Usd

  return (
    <AnimatePresence initial={false}>
      {show ? (
        <m.form
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.2, delay: 0.2 } }}
          exit={{
            opacity: 0,
            transition: { duration: 0 },
          }}
          className={tw(
            'rounded-2 border-themed-violet-800 bg-themed-violet-1000 relative flex w-full min-w-[216px] cursor-pointer flex-col overflow-hidden border p-3 text-left lg:p-4',
            isPending && 'cursor-wait',
            className,
          )}
          ref={containerRef}
          onSubmit={onSubmit}
        >
          <button
            type="submit"
            disabled={isPending}
            className="absolute inset-0 opacity-0"
            aria-hidden="true"
          />
          <div className="mb-1.5 flex w-full items-center justify-center lg:hidden">
            <Coin />
            <Coin className="-ml-1" />
            <Coin className="-ml-1" />
          </div>
          <h3
            className={tw(
              'max-lg:typography-13 max-lg:w-full max-lg:text-center max-lg:font-bold',
              'typography-24 font-extrabold leading-[24px]',
            )}
          >
            {t('studio.components.coin_top_up_message.coins_top_up.title', {
              coinAmount: coins.toLocaleString(),
            })}
          </h3>

          <p className="typography-13 mt-1 max-lg:w-full max-lg:text-center">
            <span className="font-bold">{formatCurrency(priceCents, displayCurrencyCode)}</span>
            {priceLabel}
          </p>

          <Button
            loading={isPending}
            variant="primary"
            size="sm"
            className={tw('mt-5 w-fit max-lg:w-full', isPending && 'cursor-wait')}
            type="submit"
            disabled={isPending}
          >
            <AnimatePresence mode="wait" initial={false}>
              {showSuccess ? (
                <SuccessIndicator />
              ) : (
                <m.span
                  key="default"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1, transition: { duration: 0.3 } }}
                  exit={{ opacity: 0, transition: { duration: 0.2 } }}
                >
                  {ctaLabel}
                </m.span>
              )}
            </AnimatePresence>
          </Button>

          <p className="typography-11 mt-1 text-gray-200 max-lg:w-full max-lg:text-center">
            {footerLabel}
          </p>
        </m.form>
      ) : null}
    </AnimatePresence>
  )
}
